/**
 * Walkability grid for one storey of one area.
 *
 * Walls live on tile EDGES (Sims-style), not in tiles, so movement is checked
 * per step, not per tile:
 *   - WALL_W on tile (x, y): wall on the edge between (x-1, y) and (x, y)
 *     (the tile's upper-left edge on screen, the line tx = x).
 *   - WALL_N on tile (x, y): wall on the edge between (x, y-1) and (x, y)
 *     (the tile's upper-right edge on screen, the line ty = y).
 *
 * Tile blockers are split into static (furniture, terrain) and dynamic
 * (temporary obstacles) counters so they can be toggled independently.
 */
export const WALL_W = 1;
export const WALL_N = 2;
/** Tile flag: no floor here (outside the playable area). */
export const TILE_VOID = 1;
export class TileGrid {
    width;
    height;
    /** Per-tile flags (TILE_* bits). */
    flags;
    /** Per-tile wall bits (WALL_W | WALL_N). */
    walls;
    /** Per-tile static blocker reference count (overlapping footprints are allowed). */
    staticBlock;
    /** Per-tile dynamic blocker reference count. */
    dynamicBlock;
    /** Incremented on every change, so cached paths can be revalidated cheaply. */
    version = 0;
    constructor(width, height) {
        if (width <= 0 || height <= 0 || width > 1024 || height > 1024) {
            throw new Error(`TileGrid: invalid size ${width}x${height}`);
        }
        this.width = width;
        this.height = height;
        const n = width * height;
        this.flags = new Uint8Array(n);
        this.walls = new Uint8Array(n);
        this.staticBlock = new Uint8Array(n);
        this.dynamicBlock = new Uint8Array(n);
    }
    inBounds(x, y) {
        return x >= 0 && y >= 0 && x < this.width && y < this.height;
    }
    index(x, y) {
        return y * this.width + x;
    }
    isWalkable(x, y) {
        if (!this.inBounds(x, y))
            return false;
        const i = y * this.width + x;
        return (this.flags[i] & TILE_VOID) === 0 && this.staticBlock[i] === 0 && this.dynamicBlock[i] === 0;
    }
    /** True if an orthogonal step between two adjacent tiles crosses no wall. Does not check walkability. */
    edgeOpen(x, y, dx, dy) {
        if (dx === 1)
            return this.inBounds(x + 1, y) && (this.walls[this.index(x + 1, y)] & WALL_W) === 0;
        if (dx === -1)
            return this.inBounds(x, y) && (this.walls[this.index(x, y)] & WALL_W) === 0;
        if (dy === 1)
            return this.inBounds(x, y + 1) && (this.walls[this.index(x, y + 1)] & WALL_N) === 0;
        if (dy === -1)
            return this.inBounds(x, y) && (this.walls[this.index(x, y)] & WALL_N) === 0;
        return true;
    }
    /**
     * Can an agent step from (x, y) to (x+dx, y+dy), with dx, dy in {-1, 0, 1}?
     * Diagonal steps need both orthogonal neighbours walkable and all four
     * edges around the shared corner open: no corner cutting past furniture
     * and no squeezing past the end of a wall.
     */
    canStep(x, y, dx, dy) {
        const nx = x + dx;
        const ny = y + dy;
        if (!this.isWalkable(nx, ny))
            return false;
        if (dx === 0 || dy === 0)
            return this.edgeOpen(x, y, dx, dy);
        return (this.isWalkable(nx, y) &&
            this.isWalkable(x, ny) &&
            this.edgeOpen(x, y, dx, 0) &&
            this.edgeOpen(x, y, 0, dy) &&
            this.edgeOpen(nx, y, 0, dy) &&
            this.edgeOpen(x, ny, dx, 0));
    }
    setVoid(x, y, isVoid) {
        if (!this.inBounds(x, y))
            return;
        const i = this.index(x, y);
        this.flags[i] = isVoid ? this.flags[i] | TILE_VOID : this.flags[i] & ~TILE_VOID;
        this.version++;
    }
    /** Adds or removes a wall on the W (tx = x) or N (ty = y) edge of tile (x, y). */
    setWall(x, y, edge, on) {
        if (!this.inBounds(x, y))
            return;
        const bit = edge === 'W' ? WALL_W : WALL_N;
        const i = this.index(x, y);
        this.walls[i] = on ? this.walls[i] | bit : this.walls[i] & ~bit;
        this.version++;
    }
    hasWall(x, y, edge) {
        if (!this.inBounds(x, y))
            return false;
        return (this.walls[this.index(x, y)] & (edge === 'W' ? WALL_W : WALL_N)) !== 0;
    }
    /** Reference-counted footprint blocking (static = furniture, dynamic = temporary). */
    blockFootprint(x, y, w, h, on, dynamic = false) {
        const arr = dynamic ? this.dynamicBlock : this.staticBlock;
        for (let j = y; j < y + h; j++) {
            for (let i = x; i < x + w; i++) {
                if (!this.inBounds(i, j))
                    continue;
                const k = this.index(i, j);
                arr[k] = on ? Math.min(255, arr[k] + 1) : Math.max(0, arr[k] - 1);
            }
        }
        this.version++;
    }
    isDynamicBlocked(x, y) {
        return this.inBounds(x, y) && this.dynamicBlock[this.index(x, y)] > 0;
    }
    /** Nearest walkable tile to (x, y) by ring search, or null within maxRadius. */
    nearestWalkable(x, y, maxRadius = 8) {
        if (this.isWalkable(x, y))
            return { x, y };
        for (let r = 1; r <= maxRadius; r++) {
            let best = null;
            let bestD = Infinity;
            for (let j = y - r; j <= y + r; j++) {
                for (let i = x - r; i <= x + r; i++) {
                    if (Math.max(Math.abs(i - x), Math.abs(j - y)) !== r)
                        continue;
                    if (!this.isWalkable(i, j))
                        continue;
                    const d = (i - x) * (i - x) + (j - y) * (j - y);
                    if (d < bestD) {
                        bestD = d;
                        best = { x: i, y: j };
                    }
                }
            }
            if (best)
                return best;
        }
        return null;
    }
}
